import { Component, Injector, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Address } from '../../../../models/address/address';
import { BaseModalComponent } from '@card-software/library';
import { GeoDbProxyService } from '../../../../proxies/geo-db.proxy.service';
import { EnumService } from '../../../../services/enum.service';

@Component({
  selector: 'app-address-detail-modal',
  templateUrl: './address-detail-modal.component.html',
})
export class AddressDetailModalComponent extends BaseModalComponent<Address | null> implements OnInit {
  @Input() address: Address | null = null;
  @Input() viewOnly = false;

  addressForm: FormGroup;

  countries: { label: string; value: string; code: string }[] = [];
  cities: { label: string; value: string }[] = [];
  addressTypes: { label: string; value: number }[] = [];
  selectedAddressType: number | null = null;

  constructor(
    private fb: FormBuilder,
    injector: Injector,
    private geo: GeoDbProxyService,
    private enumService: EnumService,
  ) {
    super(injector);

    this.addressForm = this.fb.group({
      addressTypes: [null, Validators.required],
      address1: ['', Validators.required],
      address2: [''],
      stateOrProvince: ['', Validators.required],
      city: ['', Validators.required],
      country: ['', Validators.required],
      zipCode: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    const addressTypeEnum = this.enumService.getEnum('AddressType');
    this.addressTypes = this.setOptionsForPrimeNg(addressTypeEnum.values);

    if (this.address) {
      this.addressForm.patchValue(this.address);

      if (this.address.addressTypes?.length) {
        this.addressForm.get('addressTypes')?.setValue(this.address.addressTypes);
      }
    }

    const countryName = this.addressForm.get('country')?.value;
    if (countryName) {
      this.geo.searchCountries(countryName).subscribe((res) => {
        this.countries = res.data.map((c) => ({
          label: c.name,
          value: c.name,
          code: c.code,
        }));

        const matched = this.countries.find((c) => c.value.toLowerCase() === countryName.toLowerCase());

        if (matched?.code) {
          const cityName = this.addressForm.get('city')?.value;
          this.geo.searchByCity(matched.code, cityName).subscribe((citiesRes) => {
            this.cities = citiesRes.data.map((city) => ({
              label: city.name,
              value: city.name,
            }));
          });
        }
      });
    } else {
      this.geo.searchCountries('').subscribe((res) => {
        this.countries = res.data.map((c) => ({
          label: c.name,
          value: c.name,
          code: c.code,
        }));
      });
    }

    this.addressForm.get('country')?.valueChanges.subscribe((name) => {
      const matched = this.countries.find((c) => c.value.toLowerCase() === name.toLowerCase());
      if (matched?.code) {
        this.loadCitiesByCountryCode(matched.code);
      } else {
        this.cities = [];
      }
    });
  }

  private setOptionsForPrimeNg(values: Record<number, string>): { label: string; value: number }[] {
    return Object.entries(values).map(([key, value]) => ({
      label: value,
      value: Number(key),
    }));
  }

  onAddressTypeChange(selectedValue: number): void {
    this.selectedAddressType = selectedValue;
    this.addressForm.get('addressTypes')?.setValue(selectedValue);
  }

  onCountrySearch(term: string) {
    this.geo.searchCountries(term).subscribe((res) => {
      this.countries = res.data.map((c) => ({
        label: c.name,
        value: c.name,
        code: c.code,
      }));
    });
  }

  onCitySearch(prefix: string) {
    const country = this.addressForm.get('country')?.value;
    const matched = this.countries.find((c) => c.value.toLowerCase() === country?.toLowerCase());
    if (matched?.code) {
      this.geo.searchByCity(matched.code, prefix).subscribe((citiesRes) => {
        this.cities = citiesRes.data.map((city) => ({
          label: city.name,
          value: city.name,
        }));
      });
    }
  }

  private loadCitiesByCountryCode(code: string) {
    this.geo.searchByCity(code, '').subscribe((citiesRes) => {
      this.cities = citiesRes.data.map((city) => ({
        label: city.name,
        value: city.name,
      }));
    });
  }

  onClose() {
    this.close({ action: 'Cancel', data: null });
  }

  onSave(): void {
    if (this.addressForm.invalid) return;

    const formValue = this.addressForm.value;

    this.close({
      action: 'Save',
      data: {
        ...formValue,
        ...(this.address?.code ? { code: this.address.code } : {}),
        addressTypes: formValue.addressTypes,
      },
    });
  }
}
